import { SpamPreviewTestStatus, type SpamPreview } from '../types/spam-preview';
import { useCallback, useEffect, useRef, useState } from 'react';
import { environment } from '@environment';

const endpoint = environment.EMAIL_PREVIEWS_API_URL;

// Fallback polling interval used while a spam test is still running, in case a
// worker-state (SignalR) notification is lost.
export const SPAM_PREVIEW_POLL_INTERVAL_MS = 15000;

// The spam test is still running (its results can still change) in these statuses.
// COMPLETED, ERROR and ABANDONED are final; UNKNOWN means there is no test.
export const isSpamTestRunning = (status: SpamPreviewTestStatus | number): boolean =>
  status === SpamPreviewTestStatus.INITIALIZING ||
  status === SpamPreviewTestStatus.TEST_INITIALIZED ||
  status === SpamPreviewTestStatus.SEED_TEST_SENT ||
  status === SpamPreviewTestStatus.PENDING;

/**
 * @param workerStateStatus latest status received from the worker-state notification.
 * @param workerStateUpdateCount incremented on every worker-state notification. The backend
 *   pushes a notification on every per-application result change while the overall status
 *   stays PENDING, so the status alone does not tell that the results changed.
 */
const useSpamFilter = (
  accountKey: string,
  mode: string,
  id: string,
  workerStateStatus: SpamPreviewTestStatus,
  workerStateUpdateCount = 0,
) => {
  const [currentSendingDomain, setCurrentSendingDomain] = useState('');
  const [spamTestStatus, setSpamTestStatus] = useState(SpamPreviewTestStatus.UNKNOWN);
  const [spamPreview, setSpamPreview] = useState<SpamPreview | null>(null);
  const [spamLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>('');
  const spamPreviewEndpoint = `${endpoint}/spam-preview/${mode}/${id}?accountKey=${accountKey}`;

  switch (workerStateStatus) {
    case SpamPreviewTestStatus.INITIALIZING:
      console.log('Spam Preview is initializing...');
      break;
    case SpamPreviewTestStatus.TEST_INITIALIZED:
      console.log('Spam Preview test is initialized.');
      break;
    case SpamPreviewTestStatus.SEED_TEST_SENT:
      console.log('Seed test sent.');
      break;
    case SpamPreviewTestStatus.PENDING:
      console.log('Spam Preview is pending.');
      break;
    case SpamPreviewTestStatus.COMPLETED:
      console.log('Spam Preview is completed.');
      break;
    case SpamPreviewTestStatus.ERROR:
      console.log('Spam Preview has an error.');
      break;
    case SpamPreviewTestStatus.ABANDONED:
      console.log('Spam Preview was abandoned.');
      break;
    case SpamPreviewTestStatus.FETCHING:
      console.log('Fetching Spam Preview.');
      break;
    default:
      break;
  }
  if (!accountKey) {
    console.error('Account key is required to fetch device previews');
  }
  if (!mode) {
    console.error('Mode is required to fetch spam previews');
  }
  if (!id) {
    console.error('Id is required to fetch spam previews');
  }

  const latestRequestRef = useRef(0);

  const fetchSpamPreview = useCallback(
    async (showLoading: boolean) => {
      const requestId = ++latestRequestRef.current;
      const isStale = () => requestId !== latestRequestRef.current;
      if (showLoading) {
        setLoading(true);
      }
      try {
        const response = await fetch(spamPreviewEndpoint, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (isStale()) return;
          setSpamPreview(data);
          setCurrentSendingDomain(data.sendingDomain);
          setSpamTestStatus(data.status);
          setError('');
        } else if (response.status === 404) {
          if (isStale()) return;
          setSpamPreview(null);
          setSpamTestStatus(SpamPreviewTestStatus.UNKNOWN);
          setError('');
        } else {
          if (isStale()) return;
          setError('Failed to fetch spam preview');
        }
        setLoading(false);
      } catch (ex) {
        if (isStale()) return;
        setError(JSON.stringify(ex));
        setLoading(false);
      }
    },
    [spamPreviewEndpoint],
  );

  useEffect(() => {
    fetchSpamPreview(true);
  }, [fetchSpamPreview, workerStateStatus, workerStateUpdateCount]);

  useEffect(() => {
    if (!isSpamTestRunning(spamTestStatus)) {
      return;
    }
    const intervalId = setInterval(() => {
      fetchSpamPreview(false);
    }, SPAM_PREVIEW_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [spamTestStatus, fetchSpamPreview]);

  useEffect(() => {
    const requestRef = latestRequestRef;
    return () => {
      requestRef.current++;
    };
  }, []);

  const refetch = useCallback(() => fetchSpamPreview(false), [fetchSpamPreview]);

  const resetInitialLoad = () => {
    setSpamPreview(null);
  };

  return {
    spamPreview,
    spamLoading,
    error,
    currentSendingDomain,
    spamTestStatus,
    setSpamPreview,
    resetInitialLoad,
    refetch,
  };
};

export default useSpamFilter;
